import React, { useState, useEffect } from "react";
import { notification } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { WorkflowService, Workflow, WorkflowTestSet } from "../../services/WorkflowService";
import { usePermissions } from "../../PermissionsProvider";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  PlayIcon,
  WorkflowIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import StepHeader from "../Evaluation/StepHeader";
import TestRunEvaluations from "./Steps/TestRunEvaluations";

export const WORKFLOW_TEST_RUN_STORAGE_KEY = "workflowTestRunData";

interface WorkflowTestRunData {
  description?: string;
  workflow_id?: number;
  evaluation_criteria_ids?: number[];
  eval_model?: string;
  json_compare_enabled?: boolean;
  field_mapping?: string;
}

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

const WorkflowTestRunNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { testSetId } = useParams<{ testSetId: string }>();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });

  const [testSet, setTestSet] = useState<WorkflowTestSet | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [data, setData] = useState<WorkflowTestRunData>(() => {
    try {
      const saved = localStorage.getItem(WORKFLOW_TEST_RUN_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(WORKFLOW_TEST_RUN_STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    if (!testSetId) return;
    WorkflowService.getWorkflowTestSet(Number(testSetId), 0, 0, () => {}, apiKey).subscribe(
      (ts: WorkflowTestSet) => {
        setTestSet(ts);
        setData((prev) => ({ ...prev }));
      },
      (err: any) => console.error(err)
    );
  }, [testSetId, apiKey]);

  useEffect(() => {
    if (!testSet) return;
    WorkflowService.getWorkflows(() => {}, apiKey).subscribe(
      (wfs: Workflow[]) => {
        const appWorkflows = (wfs || []).filter((w) => w.application_id === testSet.application_id);
        setWorkflows(appWorkflows);
        if (!data.workflow_id && appWorkflows.length > 0) {
          setData((prev) => ({ ...prev, workflow_id: appWorkflows[0].id }));
        }
      },
      () => {}
    );
  }, [testSet, apiKey]);

  useEffect(() => {
    const currentStepParam = searchParams.get("step");
    const shouldHaveStepParam = currentStep > 0 || currentStepParam !== null;
    if (shouldHaveStepParam && currentStepParam !== currentStep.toString()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      setSearchParams({ ...currentParams, step: currentStep.toString() }, { replace: true });
    }
  }, [currentStep, searchParams, setSearchParams]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const selectedWorkflow = workflows.find((w) => w.id === data.workflow_id);

  const steps: StepItem[] = [
    {
      key: "description",
      title: "Description",
      component: (
        <div className="space-y-6">
          <StepHeader
            title="Test Run Details"
            description="Provide a description for this test run to help identify its purpose."
          />
          <div className="space-y-2">
            <label className="text-sm font-gc-med text-foreground">
              Description <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={data.description || ""}
              onChange={(e) => setData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Enter test run description"
              className="min-h-[120px]"
            />
            <p className="text-xs text-muted-foreground">
              Provide a concise purpose or context for this test run.
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "workflow",
      title: "Workflow",
      component: (
        <div className="space-y-6">
          <StepHeader
            title="Select Workflow"
            description="Choose which workflow to evaluate against this test set."
          />
          <div className="space-y-2">
            <label className="text-sm font-gc-med text-foreground">
              Workflow <span className="text-red-500">*</span>
            </label>
            <Select
              value={data.workflow_id ? String(data.workflow_id) : undefined}
              onValueChange={(v) => setData((prev) => ({ ...prev, workflow_id: Number(v) }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a workflow" />
              </SelectTrigger>
              <SelectContent>
                {workflows.map((wf) => (
                  <SelectItem key={wf.id} value={String(wf.id)}>
                    <div className="flex items-center gap-2">
                      <WorkflowIcon className="w-4 h-4 text-muted-foreground" />
                      <span>{wf.workflow_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The test set items will be executed through this workflow.
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "evaluations",
      title: "Evaluations",
      component: (
        <TestRunEvaluations
          selectedCriteriaIds={data.evaluation_criteria_ids || []}
          onCriteriaChange={(ids: number[]) =>
            setData((prev) => ({ ...prev, evaluation_criteria_ids: ids }))
          }
          evalModel={data.eval_model}
          onEvalModelChange={(model: string) =>
            setData((prev) => ({ ...prev, eval_model: model }))
          }
          jsonCompareEnabled={data.json_compare_enabled || false}
          onJsonCompareChange={(enabled: boolean) =>
            setData((prev) => ({ ...prev, json_compare_enabled: enabled }))
          }
          fieldMapping={data.field_mapping || ""}
          onFieldMappingChange={(mapping: string) =>
            setData((prev) => ({ ...prev, field_mapping: mapping }))
          }
        />
      ),
    },
    {
      key: "review",
      title: "Review",
      component: (
        <div className="space-y-6">
          <StepHeader
            title="Review Test Run"
            description="Verify the details below before creating your test run."
          />
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Description</p>
                  <p className="text-sm font-gc-reg text-foreground">
                    {data.description || "No description provided"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Test Set</p>
                  <p className="text-sm font-gc-reg text-foreground">
                    {testSet?.test_set_name || `ID: ${testSetId}`}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Workflow</p>
                  <p className="text-sm font-gc-reg text-foreground">
                    {selectedWorkflow?.workflow_name || "No workflow selected"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Items</p>
                  <p className="text-sm font-gc-reg text-foreground">
                    {testSet?.item_count ?? "-"} workflow runs
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Evaluation Criteria</p>
                  <p className="text-sm font-gc-reg text-foreground">
                    {(data.evaluation_criteria_ids?.length ?? 0) > 0
                      ? `${data.evaluation_criteria_ids!.length} criteria selected`
                      : "Default (Exact Match)"}
                  </p>
                </div>
                {data.eval_model && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Evaluation Model</p>
                    <p className="text-sm font-gc-reg text-foreground">
                      {data.eval_model}
                    </p>
                  </div>
                )}
                {data.json_compare_enabled && data.field_mapping?.trim() && (
                  <div className="space-y-1 col-span-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">JSON Compare Mapping</p>
                    <pre className="text-sm font-mono font-gc-reg text-foreground bg-muted/50 rounded p-2 whitespace-pre-wrap">
                      {data.field_mapping}
                    </pre>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">
            Each test set item&apos;s input will be executed through the selected workflow. The actual output will be compared against the expected output.
          </p>
        </div>
      ),
    },
  ];

  const canProceedFromStep = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (data.description?.trim().length ?? 0) > 0;
      case 1:
        return data.workflow_id !== undefined;
      case 2:
        return true; // evaluations step is optional
      case 3:
        return true; // review
      default:
        return false;
    }
  };

  const canProceed = () => canProceedFromStep(currentStep);

  const buildConfig = (): Record<string, unknown> | undefined => {
    if (!data.json_compare_enabled || !data.field_mapping?.trim()) return undefined;
    try {
      const mapping = JSON.parse(data.field_mapping);
      return { field_mapping: mapping };
    } catch {
      return undefined;
    }
  };

  const createTestRun = () => {
    if (!testSetId || !data.workflow_id) return;
    setLoading(true);
    WorkflowService.createWorkflowTestRun(
      Number(testSetId),
      data.workflow_id,
      data.description,
      setLoading,
      apiKey,
      data.evaluation_criteria_ids,
      data.eval_model,
      buildConfig()
    ).subscribe(
      () => {
        localStorage.removeItem(WORKFLOW_TEST_RUN_STORAGE_KEY);
        notification.success({
          message: "Test run created successfully",
          description: "Your workflow test run has been scheduled.",
        });
        setLoading(false);
        navigate(`/test/workflow-test-sets/${testSetId}`);
      },
      (error: any) => {
        notification.error({ message: "Error creating test run", description: String(error) });
        setLoading(false);
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => canProceedFromStep(index)).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">

        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/test")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Test Sets
                  </button>
                </li>
                <li className="text-muted-foreground/40"><span>/</span></li>
                {testSet && (
                  <>
                    <li>
                      <button
                        onClick={() => navigate(`/test/workflow-test-sets/${testSetId}`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        {testSet.test_set_name}
                      </button>
                    </li>
                    <li className="text-muted-foreground/40"><span>/</span></li>
                  </>
                )}
                <li>
                  <span className="text-maitai-green font-gc-med">New Test Run</span>
                </li>
              </ol>
            </nav>
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                New Workflow Test Run
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Evaluate a workflow against your test set
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">{steps[currentStep].title}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep ? "bg-maitai-green" :
                          index === currentStep ? "bg-maitai-green/60" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex space-x-8">
                {steps.map((step, index) => (
                  <div
                    key={step.key}
                    className={cn(
                      "flex items-center gap-3 text-sm transition-colors",
                      index === currentStep ? "text-maitai-green" :
                      index < currentStep ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                      index === currentStep ? "bg-maitai-green text-white" :
                      index < currentStep ? "bg-maitai-green text-white" : "bg-muted text-muted-foreground"
                    )}>
                      {index < currentStep ? <CheckIcon className="w-3 h-3" /> : <span>{index + 1}</span>}
                    </div>
                    <span className="font-gc-reg">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {steps[currentStep].component}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button variant="outline" onClick={handlePrev} disabled={loading} className="gap-2">
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button onClick={handleNext} disabled={!canProceed() || loading} className="gap-2">
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button disabled={!canProceed() || loading} onClick={createTestRun} className="gap-2">
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating test run..." : (
                      <>
                        <PlayIcon className="w-4 h-4" />
                        Create Test Run
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkflowTestRunNew;
